import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2,
  MapPin,
  Navigation,
  CalendarClock,
  Car,
  RotateCcw,
  ChevronDown,
  Ruler,
  Crosshair,
  LayoutGrid,
  List,
  CalendarRange,
  Gauge,
} from "lucide-react";
import { API } from "@/config/api";
import TerritoryMap from "@/components/TerritoryMap";
import { isRepUser, repOf } from "@/components/RepField";
import StatusMatrix from "@/components/territory/StatusMatrix";
import SearchableSelect from "@/components/SearchableSelect";
import TerritoryScorecard from "@/components/territory/TerritoryScorecard";
import DoctorDrillDown from "@/components/territory/DoctorDrillDown";
import WeeklyPlannerView from "@/components/territory/WeeklyPlannerView";
import { STATUS_META } from "@/components/territory/statusMeta";

const TIER_STYLE = {
  VIP: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  Standard: "bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200",
  Low: "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200",
  Dormant: "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200",
};

const REFERRAL_LABEL = {
  has_referred: "Referring",
  lost_referring: "Stopped referring",
  never_referred: "Never referred",
};

const emptyFilters = {
  city: "",
  area: "",
  subregion: "",
  suburb: "",
  tier: "",
  status: "",
  last_visit_days: "",
  referral_status: "",
};

const VIEWS = [
  { key: "coverage", label: "Coverage", icon: Gauge },
  { key: "map", label: "Map & doctors", icon: LayoutGrid },
  { key: "plan", label: "Weekly plan", icon: CalendarRange },
];

const selectClass =
  "w-full h-10 px-3 rounded-md border border-input bg-background text-sm disabled:opacity-50";

const placeLabel = (d) => [...new Set([d.suburb, d.subregion, d.area].filter(Boolean))].join(", ");

export const hoursMinutes = (minutes) => {
  if (minutes === null || minutes === undefined) return "—";
  const total = Math.round(minutes);
  if (total < 60) return `${total}m`;
  return `${Math.floor(total / 60)}h ${String(total % 60).padStart(2, "0")}m`;
};

const STAT_CARDS = [
  { key: "shown", label: "Doctors shown", tone: "text-foreground", accent: "border-l-blue-500" },
  { key: "never", label: "Never contacted", tone: "text-red-600", accent: "border-l-red-500" },
  { key: "visits", label: "Visits logged", tone: "text-blue-600", accent: "border-l-emerald-500" },
  { key: "appointments", label: "Appointments", tone: "text-violet-600", accent: "border-l-violet-500" },
  { key: "vip", label: "VIP doctors", tone: "text-amber-600", accent: "border-l-amber-500" },
];

/** One page for everyone: reps see themselves, managers switch reps with the dropdown. */
export const RepTerritoryView = ({ user, initialRep = null }) => {
  const rep = isRepUser(user);
  const [repName, setRepName] = useState(initialRep || (rep ? repOf(user) : ""));
  const [reps, setReps] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [statFilter, setStatFilter] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [data, setData] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [view, setView] = useState("coverage");
  const [listMode, setListMode] = useState("drilldown");
  const [heatmap, setHeatmap] = useState(null);
  const [heatmapDays, setHeatmapDays] = useState(30);
  const [coverage, setCoverage] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [route, setRoute] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [assignedOnly, setAssignedOnly] = useState(true);

  useEffect(() => {
    if (rep) return;
    axios
      .get(`${API}/territory/reps`, { withCredentials: true })
      .then((res) => {
        const list = res.data.reps || [];
        setReps(list);
        setRepName((current) => current || list[0]?.rep_name || "");
      })
      .catch(() => {});
  }, [rep]);

  const load = useCallback(
    async (nextFilters, days) => {
      setLoading(true);
      const body = {
        rep_name: repName || undefined,
        assigned_only: assignedOnly,
        city: nextFilters.city || null,
        area: nextFilters.area || null,
        subregion: nextFilters.subregion || null,
        suburb: nextFilters.suburb || null,
        tier: nextFilters.tier || null,
        status: nextFilters.status || null,
        last_visit_days: nextFilters.last_visit_days ? parseInt(nextFilters.last_visit_days, 10) : null,
        referral_status: nextFilters.referral_status || null,
      };
      try {
        const [territory, heat, cover] = await Promise.all([
          axios.post(`${API}/rep/territory`, body, { withCredentials: true }),
          axios.post(
            `${API}/rep/territory/heatmap-data`,
            { rep_name: repName || undefined, days, assigned_only: assignedOnly },
            { withCredentials: true }
          ),
          axios.post(
            `${API}/rep/territory/coverage`,
            { rep_name: repName || undefined, days: 90, assigned_only: assignedOnly },
            { withCredentials: true }
          ),
        ]);
        setData(territory.data);
        setHeatmap(heat.data);
        setCoverage(cover.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to load territory");
      } finally {
        setLoading(false);
      }
    },
    [repName, assignedOnly]
  );

  // The place cascade applies to every view; status/tier stay out of it so the
  // scorecard always describes the whole area being looked at.
  const placeBody = useCallback(
    (source) => ({
      rep_name: repName || undefined,
      assigned_only: assignedOnly,
      city: source.city || null,
      area: source.area || null,
      subregion: source.subregion || null,
      suburb: source.suburb || null,
    }),
    [repName, assignedOnly]
  );

  const loadScorecard = useCallback(
    async (source) => {
      try {
        const res = await axios.post(`${API}/territory/scorecard`, placeBody(source), { withCredentials: true });
        setScorecard(res.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to load the scorecard");
      }
    },
    [placeBody]
  );

  const loadPlan = useCallback(
    async (source) => {
      setPlanLoading(true);
      try {
        const res = await axios.post(`${API}/territory/weekly-plan`, placeBody(source), { withCredentials: true });
        setPlan(res.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to build the weekly plan");
      } finally {
        setPlanLoading(false);
      }
    },
    [placeBody]
  );

  useEffect(() => {
    if (!repName && !rep) return;
    setFilters(emptyFilters);
    setStatFilter(null);
    setSelected([]);
    setRoute(null);
    setMatrix(null);
    load(emptyFilters, heatmapDays);
    loadScorecard(emptyFilters);
    setPlan(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repName]);

  useEffect(() => {
    if (view === "plan" && !plan && !planLoading) loadPlan(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Switching scope reloads everything on screen
  useEffect(() => {
    if (!repName && !rep) return;
    load(filters, heatmapDays);
    loadScorecard(filters);
    setPlan(null);
    if (view === "plan") loadPlan(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedOnly]);

  // City → Area → Sub-area → Suburb cascade
  const change = (key, value) => {
    const next = { ...filters, [key]: value };
    if (key === "city") {
      next.area = "";
      next.subregion = "";
      next.suburb = "";
    }
    if (key === "area") {
      next.subregion = "";
      next.suburb = "";
    }
    if (key === "subregion") next.suburb = "";
    setFilters(next);
    setRoute(null);
    setMatrix(null);
    load(next, heatmapDays);
    // Place filters move the goalposts for the scorecard and the plan too
    if (["city", "area", "subregion", "suburb"].includes(key)) {
      loadScorecard(next);
      if (view === "plan") loadPlan(next);
      else setPlan(null);
    }
  };

  const reset = () => {
    setFilters(emptyFilters);
    setStatFilter(null);
    setExpanded(null);
    setSelected([]);
    setRoute(null);
    setMatrix(null);
    load(emptyFilters, heatmapDays);
    loadScorecard(emptyFilters);
    setPlan(null);
    if (view === "plan") loadPlan(emptyFilters);
  };

  const changeDays = (days) => {
    setHeatmapDays(days);
    load(filters, days);
  };

  // Clicking a stat card expands it and filters the list below to match
  const clickStat = (key) => {
    if (expanded === key) {
      setExpanded(null);
      setStatFilter(null);
      if (key === "never" || key === "vip") change(key === "never" ? "last_visit_days" : "tier", "");
      return;
    }
    setExpanded(key);
    if (key === "never") {
      setStatFilter(null);
      change("last_visit_days", "999");
    } else if (key === "vip") {
      setStatFilter(null);
      change("tier", "VIP");
    } else if (key === "visits") {
      setStatFilter("visits");
    } else if (key === "appointments") {
      setStatFilter("appointments");
    } else {
      setStatFilter(null);
    }
  };

  const doctors = useMemo(() => {
    const rows = data?.doctors || [];
    if (statFilter === "visits") return rows.filter((d) => d.visit_count > 0);
    if (statFilter === "appointments") return rows.filter((d) => d.appointment_count > 0);
    return rows;
  }, [data, statFilter]);

  const summary = data?.summary;
  const options = data?.options || { cities: [], areas: [], subregions: [], suburbs: [] };
  // Counts come from the scorecard so clicking a quadrant does not zero out the other three
  const statusTotals = useMemo(() => {
    const card = scorecard?.coverage_scorecard;
    if (!card) return { total: 0 };
    return {
      green: card.green.count,
      yellow: card.yellow.count,
      blue: card.blue.count,
      red: card.red.count,
      total: card.total_doctors,
    };
  }, [scorecard]);
  const mappable = useMemo(() => doctors.filter((d) => d.latitude && d.longitude), [doctors]);
  const statValue = {
    shown: doctors.length,
    never: summary?.never_contacted ?? 0,
    visits: summary?.visits ?? 0,
    appointments: summary?.appointments ?? 0,
    vip: summary?.by_tier?.VIP ?? 0,
  };
  const statDetail = {
    shown: `${summary?.doctors_in_territory ?? 0} doctors in the territory before filters`,
    never: "Priority list — nobody has logged a visit or appointment here",
    visits: "Showing only doctors with at least one logged visit",
    appointments: "Showing only doctors with at least one appointment",
    vip: "10 or more referrals in the last 12 months",
  };

  const startRoute = async () => {
    const ids = selected.length >= 2 ? selected : mappable.slice(0, 8).map((d) => d.doctor_id);
    if (ids.length < 2) {
      toast.error("Pick at least two doctors with a known location");
      return;
    }
    setBusy("route");
    try {
      const res = await axios.post(
        `${API}/rep/territory/optimize-route`,
        { doctor_ids: ids },
        { withCredentials: true }
      );
      setRoute(res.data);
      setSelected(ids);
      toast.success(
        `${res.data.optimized_route.length} stops · ${res.data.total_distance_km}km · ${hoursMinutes(
          res.data.total_duration_minutes
        )}`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not plan the route");
    } finally {
      setBusy(null);
    }
  };

  const loadMatrix = async () => {
    const ids = selected.length >= 2 ? selected : mappable.slice(0, 8).map((d) => d.doctor_id);
    if (ids.length < 2) {
      toast.error("Pick at least two doctors first");
      return;
    }
    setBusy("matrix");
    try {
      const res = await axios.post(
        `${API}/rep/territory/distance-matrix`,
        { doctor_ids: ids },
        { withCredentials: true }
      );
      setMatrix(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not build the distance matrix");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="territory-view">
      {/* Manager rep switcher */}
      {!rep && (
        <Card>
          <CardContent className="p-4 flex flex-wrap items-end gap-3">
            <div className="min-w-[220px]">
              <Label className="text-xs">Viewing rep</Label>
              <select
                className={selectClass}
                value={repName}
                onChange={(e) => setRepName(e.target.value)}
                data-testid="territory-rep-selector"
              >
                {reps.map((r) => (
                  <option key={r.rep_name} value={r.rep_name}>
                    {r.rep_name} ({r.doctor_count} doctors)
                  </option>
                ))}
              </select>
            </div>
            <p className="text-xs text-muted-foreground pb-2">
              Switch rep to see their territory exactly as they see it
            </p>
          </CardContent>
        </Card>
      )}

      {/* View switcher */}
      <div className="flex flex-wrap items-center gap-2" data-testid="territory-view-switcher">
        {VIEWS.map(({ key, label, icon: Icon }) => (
          <Button
            key={key}
            variant={view === key ? "default" : "outline"}
            size="sm"
            onClick={() => setView(key)}
            className="gap-2"
            data-testid={`territory-view-${key}`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </Button>
        ))}
        <span className="flex-1" />
        <select
          className="h-9 px-3 rounded-md border border-input bg-background text-sm"
          value={assignedOnly ? "mine" : "all"}
          onChange={(e) => setAssignedOnly(e.target.value === "mine")}
          data-testid="territory-scope"
        >
          <option value="mine">Doctors allocated to this rep</option>
          <option value="all">Every doctor in the area</option>
        </select>
      </div>

      {/* Status matrix — the four quadrants, and the quickest way to filter by one */}
      <StatusMatrix
        totals={statusTotals}
        active={filters.status}
        windowDays={scorecard?.window_days ?? 90}
        onSelect={(next) => {
          if (next && view !== "map") setView("map");
          change("status", next);
        }}
      />
      {filters.status && (
        <p className="text-xs text-muted-foreground -mt-1" data-testid="status-filter-note">
          Showing {STATUS_META[filters.status].label.toLowerCase()} doctors — {STATUS_META[filters.status].advice}
        </p>
      )}

      {/* Expandable stat cards */}
      {view === "coverage" && (
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="territory-summary">
        {STAT_CARDS.map((card) => (
          <Card
            key={card.key}
            onClick={() => clickStat(card.key)}
            className={`cursor-pointer border-l-4 ${card.accent} transition-[box-shadow,border-color] hover:shadow-md ${
              expanded === card.key ? "ring-2 ring-blue-400" : ""
            }`}
            data-testid={`stat-card-${card.key}`}
          >
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground flex items-center justify-between">
                {card.label}
                <ChevronDown
                  className={`w-3 h-3 transition-transform ${expanded === card.key ? "rotate-180" : ""}`}
                />
              </p>
              <p className={`text-2xl font-bold ${card.tone}`}>{statValue[card.key]}</p>
              {expanded === card.key && (
                <p className="text-[11px] text-muted-foreground mt-1" data-testid={`stat-detail-${card.key}`}>
                  {statDetail[card.key]}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {/* Place cascade plus the tier and history filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">City</Label>
            <SearchableSelect
              options={options.cities}
              value={filters.city}
              onChange={(next) => change("city", next)}
              placeholder="Search cities..."
              allLabel="All cities"
              emptyMessage="No city matches"
              testId="territory-filter-city"
            />
          </div>
          <div>
            <Label className="text-xs">Area</Label>
            <SearchableSelect
              options={options.areas}
              value={filters.area}
              onChange={(next) => change("area", next)}
              placeholder="Search areas..."
              allLabel="All areas"
              emptyMessage="No area matches"
              disabled={options.areas.length === 0}
              testId="territory-filter-area"
            />
          </div>
          <div>
            <Label className="text-xs">Sub-area</Label>
            <SearchableSelect
              options={options.subregions}
              value={filters.subregion}
              onChange={(next) => change("subregion", next)}
              placeholder="Search sub-areas..."
              allLabel="All sub-areas"
              emptyMessage="No sub-area matches"
              disabled={options.subregions.length === 0}
              testId="territory-filter-subregion"
            />
          </div>
          <div>
            <Label className="text-xs">Suburb</Label>
            <SearchableSelect
              options={options.suburbs}
              value={filters.suburb}
              onChange={(next) => change("suburb", next)}
              placeholder="Search suburbs..."
              allLabel="All suburbs"
              emptyMessage="No suburb matches"
              disabled={options.suburbs.length === 0}
              testId="territory-filter-suburb"
            />
          </div>
          <div>
            <Label className="text-xs">Tier</Label>
            <select
              className={selectClass}
              value={filters.tier}
              onChange={(e) => change("tier", e.target.value)}
              data-testid="territory-filter-tier"
            >
              <option value="">All tiers</option>
              <option value="VIP">VIP (10+ referrals/yr)</option>
              <option value="Standard">Standard (3-9/yr)</option>
              <option value="Low">Low (0-2/yr)</option>
              <option value="Dormant">Dormant (6 months quiet)</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Last visit</Label>
            <select
              className={selectClass}
              value={filters.last_visit_days}
              onChange={(e) => change("last_visit_days", e.target.value)}
              data-testid="territory-filter-last-visit"
            >
              <option value="">Any time</option>
              <option value="30">Within 30 days</option>
              <option value="60">Within 60 days</option>
              <option value="90">Within 90 days</option>
              <option value="999">Never contacted</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Referrals</Label>
            <select
              className={selectClass}
              value={filters.referral_status}
              onChange={(e) => change("referral_status", e.target.value)}
              data-testid="territory-filter-referral"
            >
              <option value="">All</option>
              <option value="has_referred">Referring</option>
              <option value="lost_referring">Stopped referring</option>
              <option value="never_referred">Never referred</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={reset} className="gap-2 h-10 w-full" data-testid="territory-reset">
              <RotateCcw className="w-4 h-4" />
              Clear filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {view === "coverage" && <TerritoryScorecard data={scorecard} onSelectArea={(area) => change("area", area.area)} />}

      {view === "plan" && <WeeklyPlannerView plan={plan} loading={planLoading} onRefresh={() => loadPlan(filters)} />}

      {view === "map" && (
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={startRoute} disabled={busy === "route"} className="gap-2" data-testid="start-route-button">
          {busy === "route" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
          {selected.length >= 2 ? `Start route (${selected.length} stops)` : "Start route"}
        </Button>
        <Button
          variant="outline"
          onClick={loadMatrix}
          disabled={busy === "matrix"}
          className="gap-2"
          data-testid="distance-matrix-button"
        >
          {busy === "matrix" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ruler className="w-4 h-4" />}
          Distance matrix
        </Button>
        <Button variant="outline" onClick={() => setSelected([])} className="gap-2" data-testid="clear-selection">
          <RotateCcw className="w-4 h-4" />
          Clear selection
        </Button>
        <select
          className="h-10 px-3 rounded-md border border-input bg-background text-sm"
          value={heatmapDays}
          onChange={(e) => changeDays(parseInt(e.target.value, 10))}
          data-testid="heatmap-window"
        >
          <option value={30}>Heatmap: last 30 days</option>
          <option value={90}>Heatmap: last 90 days</option>
        </select>
        {route && (
          <span className="text-sm text-muted-foreground" data-testid="route-summary">
            {route.total_distance_km}km · {hoursMinutes(route.total_duration_minutes)} ·{" "}
            {route.source === "osrm" ? "road distances via OSRM" : "estimated offline (OSRM unreachable)"}
          </span>
        )}
      </div>
      )}

      {/* Map */}
      {view === "map" && (
      <Card>
        <CardContent className="p-4 space-y-3">
          {mappable.length === 0 ? (
            <p className="text-sm text-muted-foreground">No doctors with a known location to map yet.</p>
          ) : (
            <TerritoryMap doctors={mappable} heatmap={heatmap} route={route} />
          )}
          {heatmap && (
            <p className="text-xs text-muted-foreground" data-testid="heatmap-stats">
              Last {heatmap.days} days: {heatmap.total_visits} visits and {heatmap.total_appointments}{" "}
              appointments across {heatmap.covered_doctors} doctors · {heatmap.gap_doctors} doctors untouched
            </p>
          )}
          {route?.optimized_route && (
            <ol className="text-sm space-y-1" data-testid="route-order">
              {route.optimized_route.map((stop) => (
                <li key={`${stop.stop_number}-${stop.doctor_id || "start"}`} className="flex gap-2">
                  <span className="font-semibold text-blue-600">{stop.stop_number}.</span>
                  <span>{stop.doctor_name}</span>
                  <span className="text-muted-foreground">{placeLabel(stop)}</span>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
      )}

      {/* Distance matrix */}
      {view === "map" && matrix && (
        <Card>
          <CardContent className="p-4 space-y-3" data-testid="distance-matrix">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-sm">
                Driving times between {matrix.stops.length} doctors
                <span className="text-muted-foreground font-normal">
                  {" "}
                  · {matrix.source === "osrm" ? "OSRM road data" : "offline estimate"}
                </span>
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="p-2 text-left sticky left-0 bg-background">From \ To</th>
                    {matrix.stops.map((s, i) => (
                      <th key={s.doctor_id} className="p-2 font-medium">{i + 1}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.stops.map((row, i) => (
                    <tr key={row.doctor_id} className="border-t">
                      <td className="p-2 whitespace-nowrap sticky left-0 bg-background">
                        {i + 1}. {row.doctor_name}
                      </td>
                      {matrix.durations_minutes[i].map((minutes, j) => (
                        <td
                          key={`${i}-${j}`}
                          className={`p-2 text-center ${i === j ? "text-muted-foreground" : ""}`}
                        >
                          {i === j ? "—" : `${hoursMinutes(minutes)} / ${matrix.distances_km[i][j]}km`}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              Closest pair: {matrix.closest_pairs[0]?.from} → {matrix.closest_pairs[0]?.to} (
              {hoursMinutes(matrix.closest_pairs[0]?.minutes)}) · Furthest: {matrix.furthest_pairs[0]?.from} →{" "}
              {matrix.furthest_pairs[0]?.to} ({hoursMinutes(matrix.furthest_pairs[0]?.minutes)})
            </p>
          </CardContent>
        </Card>
      )}

      {/* Coverage analysis */}
      {view === "coverage" && coverage && (
        <Card>
          <CardContent className="p-4 space-y-3" data-testid="coverage-analysis">
            <div className="flex flex-wrap items-center gap-2">
              <Crosshair className="w-4 h-4 text-primary" />
              <p className="font-semibold text-sm">Coverage, last {coverage.days} days</p>
              <Badge variant="outline" className="text-[10px]">
                {coverage.summary.coverage_percent}% of doctors reached
              </Badge>
              <Badge variant="outline" className="text-[10px] text-red-600 border-red-300">
                {coverage.summary.areas_uncovered} of {coverage.summary.areas_total} areas untouched
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {coverage.areas.slice(0, 9).map((area) => (
                <div
                  key={area.name}
                  className="rounded-md border p-2"
                  data-testid={`coverage-area-${area.name.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium truncate">{area.name}</p>
                    <span
                      className={`text-xs font-semibold ${
                        area.coverage_percent === 0
                          ? "text-red-600"
                          : area.coverage_percent < 50
                            ? "text-amber-600"
                            : "text-emerald-600"
                      }`}
                    >
                      {area.coverage_percent}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded bg-muted mt-1.5 overflow-hidden">
                    <div
                      className={
                        area.coverage_percent === 0
                          ? "h-full bg-red-500"
                          : area.coverage_percent < 50
                            ? "h-full bg-amber-500"
                            : "h-full bg-emerald-500"
                      }
                      style={{ width: `${Math.max(area.coverage_percent, 2)}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {area.doctors} doctors · {area.never_contacted} never contacted
                    {area.vip_doctors ? ` · ${area.vip_doctors} VIP` : ""}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Doctor list — drill-down by place, or one flat list */}
      {view === "map" && (
        <div className="flex items-center gap-2" data-testid="list-mode-switcher">
          <Button
            variant={listMode === "drilldown" ? "default" : "outline"}
            size="sm"
            onClick={() => setListMode("drilldown")}
            className="gap-2"
            data-testid="list-mode-drilldown"
          >
            <LayoutGrid className="w-4 h-4" />
            By place
          </Button>
          <Button
            variant={listMode === "flat" ? "default" : "outline"}
            size="sm"
            onClick={() => setListMode("flat")}
            className="gap-2"
            data-testid="list-mode-flat"
          >
            <List className="w-4 h-4" />
            Flat list
          </Button>
          <p className="text-xs text-muted-foreground">
            {doctors.length} {doctors.length === 1 ? "doctor" : "doctors"} · tick any two or more and use Start route
          </p>
        </div>
      )}

      {view === "map" && loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : view === "map" && listMode === "drilldown" ? (
        <Card>
          <CardContent className="p-3">
            <DoctorDrillDown
              doctors={doctors}
              selected={selected}
              onSelectDoctor={(id) =>
                setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
              }
            />
          </CardContent>
        </Card>
      ) : view === "map" ? (
        <div className="space-y-2" data-testid="territory-doctor-list">
          {doctors.length === 0 && (
            <p className="text-sm text-muted-foreground py-8 text-center">No doctors match these filters.</p>
          )}
          {doctors.map((doctor) => (
            <Card
              key={doctor.doctor_id}
              className={`cursor-pointer transition-colors ${
                selected.includes(doctor.doctor_id) ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/30" : ""
              }`}
              onClick={() =>
                setSelected((prev) =>
                  prev.includes(doctor.doctor_id)
                    ? prev.filter((x) => x !== doctor.doctor_id)
                    : [...prev, doctor.doctor_id]
                )
              }
              data-testid={`territory-doctor-${doctor.doctor_id}`}
            >
              <CardContent className="p-3 flex items-start gap-3">
                <Checkbox
                  checked={selected.includes(doctor.doctor_id)}
                  onCheckedChange={() =>
                    setSelected((prev) =>
                      prev.includes(doctor.doctor_id)
                        ? prev.filter((x) => x !== doctor.doctor_id)
                        : [...prev, doctor.doctor_id]
                    )
                  }
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`territory-select-${doctor.doctor_id}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-sm">{doctor.doctor_name}</p>
                    <Badge className={`text-[10px] hover:bg-inherit ${STATUS_META[doctor.status].chip}`}>
                      {STATUS_META[doctor.status].label}
                    </Badge>
                    <Badge className={`text-[10px] hover:bg-inherit ${TIER_STYLE[doctor.tier]}`}>
                      {doctor.tier}
                    </Badge>
                    {!doctor.last_contact && (
                      <Badge variant="outline" className="text-[10px] text-red-600 border-red-300">
                        Never contacted
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" />
                    {placeLabel(doctor) || "Location unknown"}
                    {doctor.practice_name ? ` · ${doctor.practice_name}` : ""}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs">
                    <span className="flex items-center gap-1 text-blue-700 dark:text-blue-300">
                      <Car className="w-3 h-3" />
                      {doctor.visit_count} visits
                      {doctor.last_visit_date ? ` · last ${doctor.last_visit_date}` : ""}
                    </span>
                    <span className="flex items-center gap-1 text-violet-700 dark:text-violet-300">
                      <CalendarClock className="w-3 h-3" />
                      {doctor.appointment_count} appointments
                      {doctor.last_appointment_date ? ` · last ${doctor.last_appointment_date}` : ""}
                    </span>
                    <span className="text-muted-foreground">
                      {doctor.referral_count} referrals · {REFERRAL_LABEL[doctor.referral_status]}
                    </span>
                    {doctor.days_since_contact !== null && (
                      <span className="text-muted-foreground">{doctor.days_since_contact}d since contact</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default RepTerritoryView;
